// DRYE — <ann-marquee> (vanilla) — smooth marquee (no layout reads in RAF)
(function () {
  class AnnMarquee extends HTMLElement {
    static get observedAttributes() { return ['speed-s', 'gap']; }

    constructor() {
      super();
      this._speed = 22;
      this._gap = 24;

      this._raf = null;
      this._pos = 0;
      this._last = null;
      this._playing = true;
      this._reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Cached measurements (so we don't read layout in the animation loop)
      this._trackW = 0;
      this._vpW = 0;
      this._pxPerSec = 0;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = `
        <style>
          :host{
            display:block;
            overflow:hidden;
            --gap:${this._gap}px;
            --h:40px;
            --tx:#fff;
          }
          .viewport{ position:relative; width:100%; height:var(--h); }
          .track{
            position:absolute; inset:0 auto 0 0;
            display:flex; align-items:center; gap:var(--gap);
            height:var(--h);
            will-change:transform;
            transform: translate3d(0,0,0);
            color:var(--tx);
          }
          ::slotted(*){ flex:0 0 auto; }
        </style>
        <div class="viewport" part="viewport">
          <div class="track" part="track"><slot></slot></div>
        </div>
      `;

      this.$viewport = shadow.querySelector('.viewport');
      this.$track = shadow.querySelector('.track');
      this.$slot = shadow.querySelector('slot');

      // desktop hover pause (harmless on mobile)
      this._onEnter = () => { this._playing = false; };
      this._onLeave = () => { this._playing = true; if (!this._raf) this._start(); };

      // Rebuild triggers
      this._ro = new ResizeObserver(() => this._rebuild());
      this._mo = new MutationObserver(() => this._rebuild());
    }

    attributeChangedCallback(name, _oldV, newV) {
      if (name === 'speed-s') {
        const v = Number(newV);
        this._speed = isFinite(v) && v > 0 ? v : 22;
      }
      if (name === 'gap') {
        const g = Number(newV);
        this._gap = isFinite(g) && g >= 0 ? g : 24;
        this.style.setProperty('--gap', `${this._gap}px`);
      }
      if (this.isConnected) this._rebuild();
    }

    connectedCallback() {
      if (this.hasAttribute('speed-s')) {
        const v = Number(this.getAttribute('speed-s'));
        this._speed = isFinite(v) && v > 0 ? v : 22;
      }
      if (this.hasAttribute('gap')) {
        const g = Number(this.getAttribute('gap'));
        this._gap = isFinite(g) && g >= 0 ? g : 24;
        this.style.setProperty('--gap', `${this._gap}px`);
      }

      this.addEventListener('pointerenter', this._onEnter);
      this.addEventListener('pointerleave', this._onLeave);

      // observe size changes
      this._ro.observe(this.$viewport);
      this._ro.observe(this);

      // observe content changes
      this._mo.observe(this, { childList: true, subtree: true, characterData: true });

      // rebuild when images inside the slot load
      this._bindImageLoadHandlers();

      this._rebuild();
    }

    disconnectedCallback() {
      this._stop();
      this._ro.disconnect();
      this._mo.disconnect();
      this.removeEventListener('pointerenter', this._onEnter);
      this.removeEventListener('pointerleave', this._onLeave);
    }

    _bindImageLoadHandlers() {
      const assigned = this.$slot.assignedElements({ flatten: true });
      assigned.forEach(el => {
        el.querySelectorAll?.('img').forEach(img => {
          if (img.complete) return;
          img.addEventListener('load', () => this._rebuild(), { once: true });
        });
      });
    }

    _hasContent() {
      return this.$slot.assignedNodes({ flatten: true }).some(n =>
        n.nodeType === Node.ELEMENT_NODE ||
        (n.nodeType === Node.TEXT_NODE && n.textContent.trim().length)
      );
    }

    _rebuild() {
      if (!this._hasContent()) { this._stop(); return; }
      if (this._reduced) { this._stop(); return; }

      // reset (write)
      this._pos = 0;
      this.$track.style.transform = 'translate3d(0,0,0)';

      // measure ONCE per rebuild (reads)
      const vpW = Math.ceil(this.$viewport.getBoundingClientRect().width);
      const trackW = Math.ceil(this.$track.scrollWidth);

      // if something is zero (fonts not ready yet), try next frame
      if (!vpW || !trackW) {
        requestAnimationFrame(() => this._rebuild());
        return;
      }

      this._vpW = vpW;
      this._trackW = trackW;

      // start just outside right edge
      this._pos = this._vpW;

      const loopWidth = this._trackW + this._vpW;
      this._pxPerSec = loopWidth / this._speed;

      this._start();
    }

    _start() {
      if (this._raf) cancelAnimationFrame(this._raf);
      this._last = null;

      const step = (ts) => {
        if (!this._last) this._last = ts;
        const dt = (ts - this._last) / 1000;
        this._last = ts;

        if (this._playing) {
          this._pos -= this._pxPerSec * dt;

          // no layout reads here
          if (this._pos <= -this._trackW) {
            this._pos = this._vpW;
          }

          this.$track.style.transform = `translate3d(${this._pos}px,0,0)`;
        }

        this._raf = requestAnimationFrame(step);
      };

      this._raf = requestAnimationFrame(step);
    }

    _stop() {
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = null;
      this._last = null;
      if (this.$track) this.$track.style.transform = 'translate3d(0,0,0)';
    }
  }

  if (!customElements.get('ann-marquee')) {
    customElements.define('ann-marquee', AnnMarquee);
  }
})();
